import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { errorMessage } from "@/lib/api";
import { useTenantConfig } from "@/hooks/useTenantConfig";
import { FeatureToggles, CustomLabels } from "@/components/FeatureFields";
import ReadOnlyNotice, { useIsAdmin } from "@/components/ReadOnlyNotice";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { defaultFeatureConfig } from "@/lib/tenant-theme";
import { Loader2 } from "lucide-react";

/**
 * Custom Fields — step 5 of tenant setup. Feature toggles + custom label
 * overrides, persisted into featureFlags via the real /configuration PATCH.
 */
export default function AdminCustomFields() {
  const { config, loading, save, error: cfgError, setError } = useTenantConfig();
  const [featureConfig, setFeatureConfig] = useState(defaultFeatureConfig());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const admin = useIsAdmin();

  useEffect(() => {
    if (config) setFeatureConfig({ ...defaultFeatureConfig(), ...(config.featureFlags || {}) });
  }, [config]);

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await save({ featureFlags: featureConfig });
      setSaved(true);
    } catch (err) {
      const m = errorMessage(err);
      if (m) setError(m);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout variant="tenant">
      <h1 className="font-heading text-2xl font-bold">Custom Fields &amp; Labels</h1>
      <p className="mt-1 text-muted-foreground">Enable features and tailor the terminology for your portals.</p>
      <ReadOnlyNotice what="custom fields and labels" />
      {cfgError && <p className="mt-6 text-sm text-destructive" data-testid="customfields-error">{cfgError}</p>}
      {loading && <Loader2 className="mt-8 h-6 w-6 animate-spin text-primary" />}

      {!loading && (
        <div className="mt-6 max-w-4xl space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Feature toggles</CardTitle></CardHeader>
            <CardContent><FeatureToggles featureConfig={featureConfig} onChange={setFeatureConfig} /></CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Custom fields and labels</CardTitle></CardHeader>
            <CardContent><CustomLabels featureConfig={featureConfig} onChange={setFeatureConfig} /></CardContent>
          </Card>
          {admin && (
            <div className="flex items-center gap-3">
              <Button onClick={onSave} disabled={saving} data-testid="customfields-save-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
              </Button>
              {saved && <p className="text-sm text-emerald-600" data-testid="customfields-saved">Saved.</p>}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
